import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  pickRelayPlaybackUrl,
  pickRelayPlaybackWithTransport,
  startCameraRelay,
  type LiveStreamTransport,
} from "../../../api/cameras.ts";
import { ApiRequestError } from "../../../api/client.ts";
import { waitForHlsPlaylistReady } from "../../../lib/live-stream.ts";
import type { NearbyCamera } from "../../../types/nearbyCameras.ts";
import { StreamVideo } from "../StreamVideo.tsx";

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
} as const;

function IconFullscreen() {
  return (
    <svg {...iconProps}>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  );
}

function IconExitFullscreen() {
  return (
    <svg {...iconProps}>
      <path d="M9 4v5H4" />
      <path d="M15 4v5h5" />
      <path d="M9 20v-5H4" />
      <path d="M15 20v-5h5" />
    </svg>
  );
}

export type NearbyViewModalProps = {
  camera: NearbyCamera | null;
  onClose: () => void;
};

export const NearbyViewModal = memo(function NearbyViewModal({
  camera,
  onClose,
}: NearbyViewModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [playlistUrl, setPlaylistUrl] = useState<string | null>(null);
  const [streamTransport, setStreamTransport] =
    useState<LiveStreamTransport>("webrtc");
  const [streamError, setStreamError] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [isModalFs, setIsModalFs] = useState(false);
  const retryAttemptRef = useRef(0);

  useEffect(() => {
    retryAttemptRef.current = 0;
    if (!camera?.id) {
      setPlaylistUrl(null);
      setStreamError(false);
      setBootstrapError(null);
      setBootstrapLoading(false);
      return;
    }

    let cancelled = false;
    setBootstrapLoading(true);
    setPlaylistUrl(null);
    setStreamError(false);
    setBootstrapError(null);

    void (async () => {
      try {
        const urls = await startCameraRelay(camera.id);
        if (cancelled) return;

        const picked = pickRelayPlaybackWithTransport(urls);
        if (!picked?.url.trim()) {
          setBootstrapError("Stream unavailable.");
          return;
        }

        if (picked.transport === "hls") {
          const ready = await waitForHlsPlaylistReady(picked.url);
          if (cancelled) return;
          if (!ready) {
            setBootstrapError("Stream is starting — please try again.");
            return;
          }
        }

        setStreamTransport(picked.transport);
        setPlaylistUrl(picked.url);
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiRequestError ? e.message : "Could not start stream.";
        setBootstrapError(msg);
      } finally {
        if (!cancelled) setBootstrapLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [camera?.id]);

  const retryWithAlternateTransport = useCallback(async () => {
    if (!camera?.id || retryAttemptRef.current >= 1) {
      setStreamError(true);
      return;
    }
    retryAttemptRef.current += 1;
    setStreamError(false);
    setBootstrapLoading(true);
    setPlaylistUrl(null);

    try {
      const urls = await startCameraRelay(camera.id);
      const nextTransport: LiveStreamTransport =
        streamTransport === "webrtc" ? "hls" : "webrtc";
      const url = pickRelayPlaybackUrl(urls, nextTransport);
      if (!url?.trim()) {
        setStreamError(true);
        return;
      }

      if (nextTransport === "hls") {
        const ready = await waitForHlsPlaylistReady(url);
        if (!ready) {
          setStreamError(true);
          return;
        }
      }

      setStreamTransport(nextTransport);
      setPlaylistUrl(url);
    } catch {
      setStreamError(true);
    } finally {
      setBootstrapLoading(false);
    }
  }, [camera?.id, streamTransport]);

  useEffect(() => {
    if (!camera) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [camera, onClose]);

  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const syncFs = () => {
      setIsModalFs(document.fullscreenElement === modal);
    };
    syncFs();
    document.addEventListener("fullscreenchange", syncFs);
    return () => document.removeEventListener("fullscreenchange", syncFs);
  }, [camera?.id]);

  useEffect(() => {
    return () => {
      const modal = modalRef.current;
      if (modal && document.fullscreenElement === modal) {
        void document.exitFullscreen().catch(() => undefined);
      }
    };
  }, []);

  const toggleFs = useCallback(async () => {
    const el = modalRef.current;
    if (!el || typeof document === "undefined") return;
    try {
      if (document.fullscreenElement === el) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      /* ignore */
    }
  }, []);

  if (!camera) return null;

  const fsLabel = isModalFs ? "Exit fullscreen" : "Enter fullscreen";

  return createPortal(
    <div
      className="ds-nearby-view-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className={`ds-nearby-view-modal${
          isModalFs ? " ds-nearby-view-modal--fullscreen" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ds-nearby-view-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="ds-nearby-view-modal__header">
          <h2 id="ds-nearby-view-title" className="ds-nearby-view-modal__title">
            {camera.name}
          </h2>
          <div className="ds-nearby-view-modal__actions">
            <button
              type="button"
              className="ds-btn ds-btn--icon ds-btn--ghost"
              onClick={() => void toggleFs()}
              title={fsLabel}
              aria-label={fsLabel}
            >
              {isModalFs ? <IconExitFullscreen /> : <IconFullscreen />}
            </button>
            <button
              type="button"
              className="ds-btn ds-btn--icon ds-btn--ghost"
              onClick={onClose}
              aria-label="Close preview"
            >
              <svg
                width={18}
                height={18}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </header>

        <div className="ds-nearby-view-modal__stage">
          {bootstrapError ? (
            <p className="ds-nearby-view-modal__fallback" role="alert">
              {bootstrapError}
            </p>
          ) : null}
          {!bootstrapError && playlistUrl && !streamError ? (
            <StreamVideo
              key={`nearby-${camera.id}-${streamTransport}-${playlistUrl}`}
              playlistUrl={playlistUrl}
              streamType={streamTransport}
              enabled
              objectFit="contain"
              className="ds-nearby-view-modal__stream"
              videoClassName="ds-nearby-view-modal__video"
              onStreamError={() => {
                void retryWithAlternateTransport();
              }}
            />
          ) : null}
          {!bootstrapError && streamError ? (
            <p className="ds-nearby-view-modal__fallback" role="alert">
              Stream playback failed.
            </p>
          ) : null}
          {bootstrapLoading ? (
            <div className="ds-nearby-view-modal__loading" aria-hidden>
              <span className="ds-nearby-slider__spinner" />
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
});
